import type { MermaidConfig } from "mermaid";
import mermaid from "mermaid";

let initialized = false;

const BR_TAG_RE = /<br\s*\/?>/gi;

export const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export function ensureMermaidInit() {
  if (initialized) return;
  mermaid.initialize(mermaidConfig);
  initialized = true;
}

/**
 * Detect browser zoom level. Returns < 1 when zoomed out (e.g. 0.8 at 80%).
 * Only relevant on Safari where zoom breaks Mermaid text measurement.
 */
function getBrowserZoom(): number {
  if (!isSafari || typeof window === "undefined") return 1;
  const ratio = window.outerWidth / window.innerWidth;
  return Math.abs(ratio - 1) < 0.05 ? 1 : ratio;
}

/**
 * Render a Mermaid diagram with Safari zoom correction.
 *
 * Safari miscalculates SVG text dimensions at non-100% browser zoom,
 * causing Mermaid to create undersized node boxes. This workaround
 * renders into a container with a compensating CSS `zoom` so that
 * text measurement happens at effective 100% scale.
 */
export async function renderMermaidSvg(
  id: string,
  source: string
): Promise<string> {
  ensureMermaidInit();

  const sanitized = source.replace(BR_TAG_RE, "\n");
  const zoom = getBrowserZoom();

  if (zoom >= 0.95) {
    // No correction needed — render normally
    const { svg } = await mermaid.render(id, sanitized);
    return svg;
  }

  // Create an off-screen container with CSS zoom correction
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "0";
  // Slightly overcorrect (5%) to account for imprecise zoom detection
  // and edge label measurements that may differ from node label measurements
  container.style.zoom = String((1 / zoom) * 1.05);
  document.body.appendChild(container);

  try {
    const { svg } = await mermaid.render(id, sanitized, container);
    return svg;
  } finally {
    document.body.removeChild(container);
  }
}

export const mermaidConfig: MermaidConfig = {
  startOnLoad: false,
  theme: "neutral",
  securityLevel: "loose",
  fontFamily:
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  suppressErrorRendering: true,
  flowchart: {
    htmlLabels: true,
    useMaxWidth: false,
    wrappingWidth: 400,
  },
};
